import { parseISO } from 'date-fns'
import {
  CheckCircle2Icon,
  Code2Icon,
  NetworkIcon,
  TrendingUpIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { TASK_TYPE_CONFIG, TASK_TYPES } from '../data/task-config'
import { useHomework } from '../providers/homework-provider'

export function StatsView() {
  const { tasks, sprints, isCompleted, isWeekLocked } = useHomework()

  const stats = useMemo(() => {
    const unlockedSprints = sprints.filter(
      (s) => !isWeekLocked(parseISO(s.startDate)),
    )

    const completedTasks = tasks.filter((t) => isCompleted(t))
    const problemsSolved = completedTasks.filter(
      (t) => t.type === 'leetcode',
    ).length
    const designsDone = completedTasks.filter(
      (t) => t.type === 'system-design',
    ).length
    const assignedSoFar = tasks.filter((t) => {
      const sprint = sprints.find(
        (s) => t.date >= s.startDate && t.date <= s.endDate,
      )
      return sprint && !isWeekLocked(parseISO(sprint.startDate))
    })
    const completedAssigned = completedTasks.filter((t) =>
      assignedSoFar.some((a) => a.id === t.id),
    )
    const completionRate =
      assignedSoFar.length > 0
        ? Math.min(
            100,
            Math.round((completedAssigned.length / assignedSoFar.length) * 100),
          )
        : 0

    // Weekly stacked data
    const weekly = unlockedSprints.map((sprint) => {
      const counts: Record<string, number> = {}
      let total = 0
      for (const type of TASK_TYPES) {
        const c = tasks.filter(
          (t) =>
            t.type === type &&
            t.date >= sprint.startDate &&
            t.date <= sprint.endDate &&
            isCompleted(t),
        ).length
        counts[type] = c
        total += c
      }
      return { sprint: `S${sprint.number}`, counts, total }
    })

    // Cumulative problems solved per sprint
    let running = 0
    const cumulative = unlockedSprints.map((sprint) => {
      running += tasks.filter(
        (t) =>
          t.type === 'leetcode' &&
          t.date >= sprint.startDate &&
          t.date <= sprint.endDate &&
          isCompleted(t),
      ).length
      return { sprint: `S${sprint.number}`, problems: running }
    })

    // Per-type breakdown
    const breakdown = TASK_TYPES.map((type) => {
      const assigned = assignedSoFar.filter((t) => t.type === type)
      const done = assigned.filter((t) => isCompleted(t))
      return { type, assigned: assigned.length, done: done.length }
    })

    return {
      totalCompleted: completedTasks.length,
      totalAssigned: assignedSoFar.length,
      problemsSolved,
      designsDone,
      completionRate,
      weekly,
      cumulative,
      breakdown,
    }
  }, [tasks, sprints, isCompleted, isWeekLocked])

  const statCards = [
    {
      label: 'Tasks completed',
      value: stats.totalCompleted,
      sub: `of ${stats.totalAssigned} assigned`,
      icon: CheckCircle2Icon,
    },
    {
      label: 'Problems solved',
      value: stats.problemsSolved,
      sub: 'LeetCode problems',
      icon: Code2Icon,
    },
    {
      label: 'Design questions',
      value: stats.designsDone,
      sub: 'System design sessions',
      icon: NetworkIcon,
    },
    {
      label: 'Completion rate',
      value: `${stats.completionRate}%`,
      sub: 'across all sprints',
      icon: TrendingUpIcon,
    },
  ]

  const weeklyMax = Math.max(1, ...stats.weekly.map((w) => w.total))
  const cumulativeMax = Math.max(1, ...stats.cumulative.map((c) => c.problems))

  return (
    <section aria-label="Progress statistics" className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardHeader>
              <CardDescription className="flex items-center gap-1.5">
                <stat.icon className="size-3.5" aria-hidden="true" />
                {stat.label}
              </CardDescription>
              <CardTitle className="text-2xl tabular-nums lg:text-3xl">
                {stat.value}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{stat.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Tasks completed per sprint</CardTitle>
            <CardDescription>Stacked by homework type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex h-64 items-end gap-2">
              {stats.weekly.map((week) => (
                <div
                  key={week.sprint}
                  className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5"
                >
                  <div className="flex w-full flex-1 flex-col justify-end">
                    <div
                      className="flex w-full flex-col-reverse overflow-hidden rounded-md"
                      style={{
                        height: `${(week.total / weeklyMax) * 100}%`,
                        minHeight: week.total > 0 ? 4 : 0,
                      }}
                    >
                      {TASK_TYPES.map((type) => {
                        const count = week.counts[type]
                        if (!count) return null
                        return (
                          <Tooltip key={type}>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  'w-full',
                                  TASK_TYPE_CONFIG[type].dot,
                                )}
                                style={{
                                  height: `${(count / week.total) * 100}%`,
                                }}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              {TASK_TYPE_CONFIG[type].label}: {count}
                            </TooltipContent>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </div>
                  <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
                    {week.sprint}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
              {TASK_TYPES.map((type) => (
                <span
                  key={type}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <span
                    className={cn(
                      'size-2 rounded-full',
                      TASK_TYPE_CONFIG[type].dot,
                    )}
                    aria-hidden="true"
                  />
                  {TASK_TYPE_CONFIG[type].label}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Problems solved over time</CardTitle>
            <CardDescription>Cumulative LeetCode problems</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full">
              {stats.cumulative.length > 1 ? (
                <svg
                  viewBox="0 0 100 60"
                  preserveAspectRatio="none"
                  className="size-full overflow-visible"
                  role="img"
                  aria-label="Cumulative problems solved per sprint"
                >
                  <title>Cumulative problems solved per sprint</title>
                  {[0, 15, 30, 45, 60].map((y) => (
                    <line
                      key={y}
                      x1="0"
                      y1={y}
                      x2="100"
                      y2={y}
                      className="stroke-border"
                      strokeWidth="0.3"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                  {(() => {
                    const pts = stats.cumulative.map((c, i) => {
                      const x = (i / (stats.cumulative.length - 1)) * 100
                      const y = 60 - (c.problems / cumulativeMax) * 56
                      return [x, y] as const
                    })
                    const line = pts.map(([x, y]) => `${x},${y}`).join(' ')
                    const area = `0,60 ${line} 100,60`
                    return (
                      <>
                        <polygon
                          points={area}
                          className="fill-task-leetcode/15"
                        />
                        <polyline
                          points={line}
                          fill="none"
                          className="stroke-task-leetcode"
                          strokeWidth="2"
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                        />
                        {pts.map(([x, y], i) => (
                          <circle
                            key={stats.cumulative[i].sprint}
                            cx={x}
                            cy={y}
                            r="1.4"
                            className="fill-task-leetcode"
                            vectorEffect="non-scaling-stroke"
                          />
                        ))}
                      </>
                    )
                  })()}
                </svg>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Not enough data yet
                </div>
              )}
            </div>
            <div className="mt-3 flex justify-between text-[10px] font-medium tabular-nums text-muted-foreground">
              {stats.cumulative.map((c) => (
                <span key={c.sprint}>{c.sprint}</span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Breakdown by type</CardTitle>
          <CardDescription>
            Completed vs. assigned across all unlocked sprints
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {stats.breakdown.map(({ type, assigned, done }) => {
              const config = TASK_TYPE_CONFIG[type]
              const Icon = config.icon
              const pct = assigned > 0 ? Math.round((done / assigned) * 100) : 0
              return (
                <div key={type} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'flex size-7 items-center justify-center rounded-md',
                        config.chip,
                      )}
                    >
                      <Icon className="size-4" aria-hidden="true" />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {config.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {done}/{assigned} done
                      </span>
                    </div>
                  </div>
                  <div
                    className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${config.label} completion`}
                  >
                    <div
                      className={cn('h-full rounded-full', config.dot)}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </section>
  )
}
